import React from 'react';
import '../../../styles/PrintProgram.css';
import { formatCurrency } from '../ProgramForm/helpers/quotationCalculations';

const PrintProgram = ({
  program,
  lang = 'en',
  clientInfo = null,
  pdfSections = null,
  includePrices = true
}) => {
  console.log("🚀 ~ PrintProgram ~ clientInfo:", clientInfo)
  // ✅ تحويل الصورة إلى Base64
  const coverImageUrl = program?.cover_image?.url;

  // ==================== Helper Functions ====================

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString(
        lang === 'ar' ? 'ar-EG' : 'en-US',
        { day: 'numeric', month: 'long', year: 'numeric' }
      );
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    try {
      const timeStr = String(timeString).trim();

      if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
        return timeStr.substring(0, 5);
      }

      if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeStr)) {
        return timeStr.substring(0, 5);
      }

      let cleanTime = timeStr.toUpperCase();
      const ampm = cleanTime.match(/(AM|PM)$/) ? cleanTime.slice(-2) : '';

      if (ampm) {
        cleanTime = cleanTime.replace(/\s*(AM|PM)$/i, '').trim();
      }

      const timeParts = cleanTime.split(':');
      let hours = parseInt(timeParts[0]) || 0;
      const minutes = parseInt(timeParts[1]) || 0;

      if (ampm === 'PM' && hours !== 12) {
        hours += 12;
      } else if (ampm === 'AM' && hours === 12) {
        hours = 0;
      }

      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');

      return `${formattedHours}:${formattedMinutes}`;
    } catch (error) {
      console.warn('Error formatting time:', timeString, error);
      return String(timeString).trim() || '-';
    }
  };

  const getProductTime = (product) => {
    if (!product) return '-';
    const timeValue = product.time || product.pickup_time;
    return formatTime(timeValue);
  };

  const t = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field.en || field.ar || '';
  };

  const getPaxQty = (searchName) => {
    const paxTypes = program.pax_types || program.paxtypes || [];

    if (!paxTypes || paxTypes.length === 0) {
      return 0;
    }

    const paxType = paxTypes.find((p) => {
      const nameEn = p.name?.en || p.name_en || '';
      const nameAr = p.name?.ar || p.name_ar || '';
      const nameString = typeof p.name === 'string' ? p.name : '';

      const searchLower = searchName.toLowerCase();

      return (
        nameEn.toLowerCase() === searchLower ||
        nameAr === searchName ||
        nameString.toLowerCase() === searchLower ||
        nameEn.toLowerCase().includes(searchLower) ||
        nameString.toLowerCase().includes(searchLower)
      );
    });

    return paxType?.quantity ?? paxType?.count ?? 0;
  };

  const getProductSummary = (product) => {
    if (!product) return '';
    const field = product.summary || product.short_description;
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field.en || field.ar || '';
  };

  // const getPricingCode = (product) => {
  //   if (!product) return '';
  //   return product.pricing_code || product.pricingCode || '';
  // };

  // const getProductCity = (product) => {
  //   if (!product) return '';
  //   const cityObj = product.city;
  //   if (!cityObj) return '';
  //   if (typeof cityObj === 'string') return cityObj;
  //   return cityObj.name?.[lang] || cityObj.name?.en || cityObj.name?.ar || '';
  // };

  // const isDayLarge = (day) => {
  //   return day.products && day.products.length > 5;
  // };

  const formatCities = (cities, locale) => {
    if (!cities || !Array.isArray(cities) || cities.length === 0) return '';
    const names = cities
      .sort((a, b) => a.order - b.order)
      .map(c => `\u2068${c.name?.[locale] || c.name?.en || c.name?.ar || ''}\u2069`);

    if (names.length === 0) return '';
    if (names.length === 1) return names[0];

    const separator = locale === 'ar' ? '، ' : ', ';
    const lastSeparator = locale === 'ar' ? ' و' : ' & ';

    return names.slice(0, -1).join(separator) + lastSeparator + names[names.length - 1];
  };

  // ==================== النصوص المترجمة ====================

  const translations = {
    en: {
      companyName: 'Adventures Club',
      companyTagline: 'Travel & Tours',
      overview: 'Program Overview',
      travelDates: 'Travel Dates',
      duration: 'Duration',
      travelers: 'Travelers',
      days: 'Days',
      nights: 'Nights',
      people: 'People',
      child: 'Child',
      infant: 'Infant',
      adults: 'Adults',
      day: 'Day',
      activities: 'Activities',
      time: 'Time',
      service: 'Service',
      summary: 'Summary',
      city: 'City',
      units: 'Units',
      nightsCount: 'Nights',
      programPrice: 'Program Price',
      currency: '฿',
      clientAgentInfo: 'Client & Agent Information',
      agentName: 'Agent Name',
      agentCountry: 'Agent Country',
      clientName: 'Client Name',
      phoneNumber: 'Phone Number',
      clientNationality: 'Client Nationality',
      cities: 'Cities',
      requestDate: 'Request Date',
      sendingDate: 'Sending Date',
      requestNumber: 'Request Number',
      itinerary: 'Program Itinerary',
      flightConnections: 'Flight Connections',
      hotelAccommodation: 'Hotel Accommodation',
      checkIn: 'Check-In',
      checkOut: 'Check-Out',
      confirmationNo: 'Confirmation No',
      roomCategory: 'Room Category',
      mealPlan: 'Meal Plan',
      baggage: 'Baggage',
      paxCount: 'Pax',
    },
    ar: {
      companyName: 'Adventures Club',
      companyTagline: 'سفر وسياحة',
      overview: 'نظرة عامة على البرنامج',
      travelDates: 'تواريخ السفر',
      duration: 'المدة',
      travelers: 'المسافرون',
      days: 'أيام',
      nights: 'ليالي',
      people: 'شخص',
      child: 'طفل',
      infant: 'رضيع',
      adults: 'بالغين',
      day: 'اليوم',
      activities: 'الأنشطة',
      time: 'الوقت',
      service: 'الخدمة',
      summary: 'الوصف',
      city: 'المدينة',
      units: 'الوحدات',
      nightsCount: 'الليالي',
      programPrice: 'سعر البرنامج',
      currency: '฿',
      clientAgentInfo: 'معلومات العميل والوكالة',
      agentName: 'اسم الوكالة',
      agentCountry: 'دولة الوكالة',
      clientName: 'اسم العميل',
      phoneNumber: 'رقم الهاتف',
      clientNationality: 'جنسية العميل',
      cities: 'المدن',
      requestDate: 'تاريخ الطلب',
      sendingDate: 'تاريخ الإرسال',
      requestNumber: 'رقم الطلب',
      itinerary: 'برنامج الرحلة',
      flightConnections: 'رحلات الطيران',
      hotelAccommodation: 'أماكن الإقامة بالفنادق',
      checkIn: 'تسجيل الوصول',
      checkOut: 'تسجيل المغادرة',
      confirmationNo: 'رقم التأكيد',
      roomCategory: 'فئة الغرفة',
      mealPlan: 'خطة الوجبة',
      baggage: 'الأمتعة',
      paxCount: 'العدد',
    },
  };

  const texts = translations[lang] || translations.en;

  // ==================== استخراج البيانات ====================

  const title = t(program.title);
  // const description = t(program.description);
  // const programNumber = program.program_number || program.programNumber || '';

  // Extract all flights and hotels across all days
  const flights = (program.days || []).reduce((acc, day) => {
    const dayFlights = (day.products || []).filter(p => p.product_type === 'flight' || p.productType === 'flight' || p.category_id === 5);
    const mappedFlights = dayFlights.map(f => ({
      ...f,
      departureDate: f.flight_details?.departure_date || day.date || day.start_date,
      arrivalDate: f.flight_details?.arrival_date || f.flight_details?.departure_date || day.date || day.start_date
    }));
    return [...acc, ...mappedFlights];
  }, []);
  flights.sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));

  const hotels = (program.days || []).reduce((acc, day) => {
    const dayHotels = (day.products || []).filter(p => (p.product_type === 'hotel' || p.productType === 'hotel' || p.category_id === 3) && p.hotel_details);
    const mappedHotels = dayHotels.map(h => {
      const checkIn = h.hotel_details.check_in_date || h.check_in_date || day.date || day.start_date;
      const roomStays = h.hotel_details?.room_stays || [];
      const nights = h.nights || h.hotel_details.nights || h.hotel_details.total_room_nights || roomStays.length || 1;
      let checkOut = h.hotel_details.check_out_date || h.check_out_date;
      if (!checkOut && roomStays.length > 0) {
        // Calculate checkout from last room_stay date + 1 day (checkout is always the day after the last night)
        const sortedStays = [...roomStays].sort((a, b) => a.date.localeCompare(b.date));
        const lastStayDate = sortedStays[sortedStays.length - 1].date;
        const d = new Date(lastStayDate);
        d.setDate(d.getDate() + 1);
        checkOut = d.toISOString().split('T')[0];
      } else if (!checkOut && checkIn) {
        const d = new Date(checkIn);
        d.setDate(d.getDate() + parseInt(nights));
        checkOut = d.toISOString().split('T')[0];
      }
      return {
        ...h,
        checkIn,
        checkOut,
        nights
      };
    });
    return [...acc, ...mappedHotels];
  }, []);

  hotels.sort((a, b) => (a.checkIn || '').localeCompare(b.checkIn || ''));

  // Helper function to get the correct price and currency
  const getDisplayPrice = () => {
    const price = program.display_price || program.price;
    const currency = program.display_currency || program.currency;
    return {
      price: price ?? 0,
      symbol: currency?.symbol || '฿',
      name: currency?.name?.[lang] || currency?.name?.en || 'THB'
    };
  };

  const { price: totalPrice } = getDisplayPrice();

  const startDate = program.start_date || program.startDate;
  const endDate = program.end_date || program.endDate;
  const daysCount = program.durations || program.days?.length || 0;
  const nightsCount = program.nights || 0;
  // const travelersCount = program.number_of_pax || program.numberOfPax || 0;

  const childCount = getPaxQty('child');
  const infantCount = getPaxQty('infant');
  const adultCount = getPaxQty('adult') || getPaxQty('adults');

  const days = program.days || [];

  // ==================== JSX ====================

  return (
    <div className="print-document" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* ✅ ==================== MODERN COVER PAGE - REFACTORED ==================== */}
      <div className="print-cover-page-new">
        <div className="cover-bg-wrapper">
          <img
            src={coverImageUrl || "https://testapi.ztripplus.com/storage/1146/conversions/6a3117e14e6fe_images_(1)-preview.jpg"}
            alt="Cover Background"
            className="cover-bg-img"
          />
          <div className="cover-bg-overlay"></div>
        </div>

        <div className="cover-center-content">
          <h1 className="cover-main-title">
            {title || 'Exclusive Travel Program'}
          </h1>

          <div className="cover-badges-row">
            <div className="cover-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
              <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
            </div>
            <div className="cover-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fdb813" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>
              <span>{daysCount} {texts.days}</span>
              <span className="badge-divider">/</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a4b0be" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
              <span>{nightsCount} {texts.nightsCount || texts.nights}</span>
            </div>
          </div>

          <div className="cover-location">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            <span>{clientInfo?.cities || formatCities(program.cities, lang) || program.destination || ''}</span>
          </div>
        </div>

        <div className="cover-floating-card">
          <div className="fc-border-left"></div>
          <div className="fc-content">
            <div className="fc-top">
              <div className="fc-col">
                <span className="fc-label">{texts.agentName || 'AGENT NAME'}</span>
                <span className="fc-value-small">{clientInfo?.agent_name || '-'}</span>
              </div>
              <div className="fc-col">
                <span className="fc-label">{texts.confirmationNo || 'CONFIRMATION NUMBER'}</span>
                <span className="fc-value-small">{program.confirmation_number || '-'}</span>
              </div>
              <div className="fc-col">
                <span className="fc-label">{texts.requestNumber || 'REQUEST NUMBER'}</span>
                <span className="fc-value-small">{program.request_number || '-'}</span>
              </div>
            </div>

            <div className="fc-divider"></div>

            <div className="fc-bottom">
              <div className="fc-client-info">
                <span className="fc-label">{texts.clientName || 'CLIENT NAME'}</span>
                <span className="fc-client-name">{clientInfo?.client_name || '-'}</span>
                <div className="fc-pax-row">
                  <div className="fc-pax-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdb813" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    <span>{adultCount} {texts.adults}</span>
                  </div>
                  <div className="fc-pax-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdb813" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    <span>{childCount} {texts.child}</span>
                  </div>
                  <div className="fc-pax-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdb813" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    <span>{infantCount} {texts.infant}</span>
                  </div>
                </div>
              </div>

              {includePrices && totalPrice > 0 && (
                <div className="fc-price-info">
                  <span className="fc-label">{lang === 'ar' ? 'السعر الإجمالي' : 'TOTAL PRICE'}</span>
                  <span className="fc-price-value">{formatCurrency(totalPrice)} {getDisplayPrice().name}</span>
                  <span className="fc-price-tax">{lang === 'ar' ? 'شامل جميع الضرائب والخدمات' : 'Inclusive of all taxes & services'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== Header ==================== */}
      {/* <div className="print-header">
        <div className="header-left">
          <h1 className="company-name">{texts.companyName}</h1>
          <p className="company-tagline">{texts.companyTagline}</p>
        </div>
        <div className="header-right">
          <span className="program-number">{programNumber}</span>
          <span className="print-date">{formatDate(new Date())}</span>
        </div>
      </div> */}

      {/* ==================== Program Title ==================== */}
      {/* <div className="program-title">
        <h2>{title || 'Program'}</h2>
        {description && <p className="program-desc">{description}</p>}
      </div> */}

      {/* ✅ ==================== Client & Agent Information ==================== */}
      {/* {clientInfo && (
        <div className="client-agent-section">
          <h3 className="section-title">{texts.clientAgentInfo}</h3>
          <div className="info-cards-grid">
            
            <div className="info-card agent-card">
              <div className="card-header">
                {lang === 'ar' ? 'معلومات الوكالة' : 'Agent Information'}
              </div>
              <div className="card-body">
                {clientInfo.agent_name && (
                  <div className="info-row">
                    <span className="info-label">{texts.agentName}:</span>
                    <span className="info-value">{clientInfo.agent_name}</span>
                  </div>
                )}
                {clientInfo.agent_country && (
                  <div className="info-row">
                    <span className="info-label">{texts.agentCountry}:</span>
                    <span className="info-value">{clientInfo.agent_country}</span>
                  </div>
                )}
                {clientInfo.req_date && (
                  <div className="info-row">
                    <span className="info-label">{texts.requestDate}:</span>
                    <span className="info-value">{formatDate(clientInfo.req_date)}</span>
                  </div>
                )}
                {clientInfo.sending_date && (
                  <div className="info-row">
                    <span className="info-label">{texts.sendingDate}:</span>
                    <span className="info-value">{formatDate(clientInfo.sending_date)}</span>
                  </div>
                )}
              </div>
            </div>

            
            <div className="info-card client-card">
              <div className="card-header">
                {lang === 'ar' ? 'معلومات العميل' : 'Client Information'}
              </div>
              <div className="card-body">
                {clientInfo.client_name && (
                  <div className="info-row">
                    <span className="info-label">{texts.clientName}:</span>
                    <span className="info-value">{clientInfo.client_name}</span>
                  </div>
                )}
                {clientInfo.phone_number && (
                  <div className="info-row">
                    <span className="info-label">{texts.phoneNumber}:</span>
                    <span className="info-value" dir={lang === 'ar' ? 'ltr' : 'auto'}>
                      {clientInfo.phone_number}
                    </span>
                  </div>
                )}
                {clientInfo.client_nationality && (
                  <div className="info-row">
                    <span className="info-label">{texts.clientNationality}:</span>
                    <span className="info-value">{clientInfo.client_nationality}</span>
                  </div>
                )}
                {clientInfo.cities && (
                  <div className="info-row">
                    <span className="info-label">{texts.cities}:</span>
                    <span className="info-value">{clientInfo.cities}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* ==================== Program Overview ==================== */}
      {/* <div className="overview-section">
        <h3 className="section-title">{texts.overview}</h3>
        <div className="overview-grid">
          <div className="info-item">
            <span className="label">{texts.travelDates}</span>
            <span className="value">
              {formatDate(startDate)} → {formatDate(endDate)}
            </span>
          </div>
          <div className="info-item">
            <span className="label">{texts.duration}</span>
            <span className="value">
              {daysCount} {texts.days} / {nightsCount} {texts.nights}
            </span>
          </div>
          <div className="info-item">
            <span className="label">{texts.travelers}</span>
            <span className="value">{travelersCount} {texts.people}</span>
          </div>
          <div className="info-item">
            <span className="label">{texts.travelers}</span>
            <span className="value">
              {adultCount} {texts.adults} • {childCount} {texts.child} • {infantCount} {texts.infant}
            </span>
          </div>
        </div>

        
        <div className="travelers-table" style={{ marginTop: '8px' }}>
          <table>
            <thead>
              <tr>
                <th>{texts.adults}</th>
                <th>{texts.child}</th>
                <th>{texts.infant}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{adultCount}</td>
                <td>{childCount}</td>
                <td>{infantCount}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div> */}

      {/* ==================== Flight Connections ==================== */}
      {flights && flights.length > 0 && (
        <div className="flight-connections-section">
          <h3 className="section-title-new">{texts.flightConnections}</h3>
          <div className="flight-grid-new">
            {flights.map((flight, idx) => {
              const details = flight.flight_details || {};
              const airlineName = details.airline?.name?.[lang] || details.airline?.name?.en || '';
              const depAirport = details.departure_airport || {};
              const arrAirport = details.arrival_airport || {};
              const depCity = depAirport.city?.name?.[lang] || depAirport.city?.name?.en || '';
              const arrCity = arrAirport.city?.name?.[lang] || arrAirport.city?.name?.en || '';

              return (
                <div key={idx} className="flight-card-new">
                  {/* Header: Date and Airline */}
                  <div className="fc-new-header">
                    <div className="fc-new-date">
                      {formatDate(flight.departureDate).toUpperCase()}
                    </div>
                    <div className="fc-new-airline">
                      {airlineName}
                    </div>
                  </div>

                  {/* Route: City (Code) -> City */}
                  <div className="fc-route-title">
                    {depCity} {depAirport.code ? `(${depAirport.code})` : ''}
                    <span className="fc-arrow"> {lang === 'ar' ? '←' : '→'} </span>
                    {arrCity} {arrAirport.code ? `(${arrAirport.code})` : ''}
                  </div>

                  {/* Body: Times and Center Line */}
                  <div className="fc-new-body">
                    <div className="fc-time-col">
                      <span className="fc-time">{formatTime(details.departure_time)}</span>
                      <span className="fc-airport">{depAirport.code || depCity}</span>
                    </div>

                    <div className="fc-route-middle">
                      <span className="fc-flight-no">{details.flight_number || ''}</span>
                      <div className="fc-route-line"></div>
                      <span className="fc-arrow-icon">{lang === 'ar' ? '←' : '→'}</span>
                    </div>

                    <div className="fc-time-col right">
                      <span className="fc-time">{formatTime(details.arrival_time)}</span>
                      <span className="fc-airport">{arrAirport.code || arrCity}</span>
                    </div>
                  </div>

                  {/* Footer: PNR and Baggage */}
                  <div className="fc-new-footer">
                    <div className="fc-footer-item">
                      PNR: <span className="fc-footer-val">{details.pnr || '-'}</span>
                    </div>
                    <div className="fc-footer-item">
                      Baggage: <span className="fc-footer-val">{details.baggage_weight || '-'} {details.baggage_unit || 'Kg'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==================== Hotel Accommodation ==================== */}
      {hotels && hotels.length > 0 && (
        <div className="hotel-accommodation-section">
          <h3 className="section-title-new">{texts.hotelAccommodation}</h3>
          <div className="hotel-grid-new">
            {hotels.map((hotel, idx) => {
              const details = hotel.hotel_details || {};
              const cityName = details.city?.name?.[lang] || details.city?.name?.en || '';
              const stars = details.rating?.stars || 0;
              const mealPlanName = details.meal_plan?.name?.[lang] || details.meal_plan?.name?.en || '';
              const roomCategoryName = details.room_category?.name?.[lang] || details.room_category?.name?.en || '';
              // const confNumber = details.confirmation_number || program.confirmation_number || '';
              const imageUrl = details.image?.url || program.cover_image?.url || "https://testapi.ztripplus.com/storage/1146/conversions/6a3117e14e6fe_images_(1)-preview.jpg";

              return (
                <div key={idx} className="hotel-card-new">
                  <div className="hc-image-wrapper">
                    <img src={imageUrl} alt="Hotel" className="hc-image" />
                  </div>

                  <div className="hc-content">
                    <div className="hc-header">
                      {cityName && <span className="hc-city-badge">{cityName}</span>}
                      <div className="hc-stars">
                        {Array.from({ length: stars }).map((_, i) => (
                          <svg key={i} viewBox="0 0 24 24" width="16" height="16" fill="#fdb813">
                            <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        ))}
                      </div>
                    </div>

                    <h4 className="hc-hotel-name">{details.hotel_name}</h4>

                    <div className="hc-dates-row">
                      <div className="hc-date-box">
                        <span className="hc-date-label">CHECK-IN</span>
                        <span className="hc-date-val">{formatDate(hotel.checkIn)}</span>
                      </div>
                      <div className="hc-date-box">
                        <span className="hc-date-label">CHECK-OUT</span>
                        <span className="hc-date-val">{formatDate(hotel.checkOut)}</span>
                      </div>
                    </div>

                    <div className="hc-divider"></div>

                    <div className="hc-details-list">
                      <div className="hc-detail-item">
                        <svg className="hc-detail-icon green" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <path d="M9 15l2 2 4-4"></path>
                        </svg>
                        <span>Confirmation No: <strong style={{ color: '#f59e0b' }}>{hotel.confirmation_number || program.confirmation_number || '-'}</strong></span>
                      </div>

                      {roomCategoryName && (
                        <div className="hc-detail-item">
                          <svg className="hc-detail-icon blue" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                            <path d="M9 22v-4h6v4"></path>
                            <path d="M8 6h.01"></path>
                            <path d="M16 6h.01"></path>
                            <path d="M12 6h.01"></path>
                            <path d="M12 10h.01"></path>
                            <path d="M12 14h.01"></path>
                            <path d="M16 10h.01"></path>
                            <path d="M16 14h.01"></path>
                            <path d="M8 10h.01"></path>
                            <path d="M8 14h.01"></path>
                          </svg>
                          <span>Room Category: <strong>{roomCategoryName}</strong></span>
                        </div>
                      )}

                      {mealPlanName && (
                        <div className="hc-detail-item">
                          <svg className="hc-detail-icon orange" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
                            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
                            <line x1="6" y1="1" x2="6" y2="4"></line>
                            <line x1="10" y1="1" x2="10" y2="4"></line>
                            <line x1="14" y1="1" x2="14" y2="4"></line>
                          </svg>
                          <span>Meal Plan: <strong>{mealPlanName}</strong></span>
                        </div>
                      )}
                    </div>

                    <div className="hc-footer">
                      <span className="hc-nights-badge">
                        {hotel.nights} {texts.nightsCount}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==================== Program Itinerary (Activities) ==================== */}
      {days && days.length > 0 && (
        <div className="itinerary-section">
          <h3 className="section-title-new">{lang === 'ar' ? 'جدول الرحلات والتنقلات' : 'Tour And Transfer Schedule'}</h3>

          <div className="tl-container">
            {/* Build flat list of events */}
            {(() => {
              const timelineEvents = [];
              days.forEach((day, dayIndex) => {
                const dayNumber = dayIndex + 1;
                const dateStr = formatDate(day.date || day.start_date);
                const cityName = day.city?.name?.[lang] || day.city?.name?.en || day.title || '';

                let products = (day.products || []).filter(p => {
                  const isHotel = p.product_type === 'hotel' || p.productType === 'hotel' || p.category_id === 3;
                  return !isHotel; // Keep flights and all other activities
                });

                if (products.length === 0) {
                  timelineEvents.push({ type: 'free_day', dayNumber, dateStr, cityName });
                } else {
                  products.forEach(prod => {
                    timelineEvents.push({ type: 'product', dayNumber, dateStr, cityName, product: prod });
                  });
                }
              });

              return timelineEvents.map((ev, i) => {
                let title = '';
                let desc = '';
                let badgeType = '';
                let timeStr = '';
                let cityBadgeText = ev.cityName;
                let mainIcon = null;
                let isFreeDay = ev.type === 'free_day';
                
                // If it's a product, check if it's actually a "Free Time" or "Free Day"
                if (!isFreeDay && ev.product) {
                  const prodTitle = ev.product.title?.en?.toLowerCase() || ev.product.title?.ar || '';
                  if (prodTitle.includes('free') || prodTitle.includes('حر') || prodTitle.includes('leisure')) {
                    isFreeDay = true;
                  }
                }

                if (isFreeDay) {
                  const baseTitle = ev.product ? t(ev.product.title) : (lang === 'ar' ? 'يوم حر' : 'Free Day');
                  title = baseTitle;
                  desc = lang === 'ar' 
                    ? `استمتع بيوم كامل في ${cityBadgeText || 'المدينة'} بكل راحة وحرية.` 
                    : `Enjoy a full day in ${cityBadgeText || 'the city'} at your leisure.`;
                  badgeType = 'Leisure';
                  timeStr = ''; // empty to hide the badge completely
                  mainIcon = (
                    <svg className="tl-main-icon-svg purple" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                  );
                } else {
                  const prod = ev.product;
                  title = t(prod.title);
                  desc = getProductSummary(prod);
                  timeStr = getProductTime(prod);

                  let pType = prod.product_type || prod.productType || '';
                  if (!pType && prod.category_id) {
                    switch (prod.category_id) {
                      case 1: pType = 'tour'; break;
                      case 2: pType = 'transfer'; break;
                      case 4: pType = 'visa'; break;
                      case 5: pType = 'flight'; break;
                      default: pType = 'activity';
                    }
                  }
                  badgeType = pType.charAt(0).toUpperCase() + pType.slice(1);

                  // Inter-city logic
                  if (pType === 'flight' && prod.flight_details) {
                    const fd = prod.flight_details;
                    
                    // Format flight time
                    const dTime = fd.departure_time ? fd.departure_time.substring(0, 5) : '';
                    const aTime = fd.arrival_time ? fd.arrival_time.substring(0, 5) : '';
                    if (dTime && aTime) {
                      timeStr = `${dTime} ${lang === 'ar' ? '←' : '→'} ${aTime}`;
                    } else if (dTime) {
                      timeStr = dTime;
                    } else if (aTime) {
                      timeStr = aTime;
                    } else {
                      timeStr = '';
                    }

                    const dCity = fd.departure_airport?.city?.name?.[lang] || fd.departure_airport?.city?.name?.en;
                    const aCity = fd.arrival_airport?.city?.name?.[lang] || fd.arrival_airport?.city?.name?.en;
                    if (dCity && aCity) {
                      cityBadgeText = <>{dCity} <span className="tl-arrow-span">{lang === 'ar' ? '←' : '→'}</span> {aCity}</>;
                    }
                    
                    const dAirport = prod.flight_details.departure_airport?.name?.[lang] || prod.flight_details.departure_airport?.name?.en || dCity || '';
                    const aAirport = prod.flight_details.arrival_airport?.name?.[lang] || prod.flight_details.arrival_airport?.name?.en || aCity || '';
                    if (dAirport && aAirport) {
                      desc = lang === 'ar' 
                        ? `انتقال من ${dAirport} إلى ${aAirport}`
                        : `Transfer from ${dAirport} to ${aAirport}`;
                    }
                  } else if (pType === 'transfer') {
                    // Try to guess from title if no explicit fields
                    const titleEn = prod.title?.en?.toLowerCase() || '';
                    if (titleEn.includes(' krabi ') && titleEn.includes(' phuket ')) {
                      cityBadgeText = <>Krabi <span className="tl-arrow-span">→</span> Phuket</>;
                    } else if (titleEn.includes(' to ')) {
                      // simple heuristic, fallback to city
                    }
                  }

                  if (pType === 'flight') {
                    mainIcon = (
                      <svg className="tl-main-icon-svg purple" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: lang === 'ar' ? 'rotate(180deg)' : 'none' }}>
                        <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.2-1.1.6L3 8l6 5-3 3-3.2-1.1c-.4-.1-.8.2-.9.6L1.5 17l4.5 1.5 1.5 4.5c.1.3.4.7.9.6L9 22l-1.1-3.2 3-3 5 6c.4.3.8.1.9-.4z"></path>
                      </svg>
                    );
                  } else if (pType === 'transfer') {
                    mainIcon = (
                      <svg className="tl-main-icon-svg purple" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    );
                  } else {
                    mainIcon = (
                      <svg className="tl-main-icon-svg purple" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    );
                  }
                }

                return (
                  <div key={i} className="tl-event-row">
                    <div className="tl-left-col">
                      <div className="tl-dot"></div>
                      {i !== timelineEvents.length - 1 && <div className="tl-line"></div>}
                    </div>
                    <div className="tl-card">
                      <div className="tl-card-header">
                        <div className="tl-card-header-left">
                          <span className="tl-day-badge">DAY {ev.dayNumber}</span>
                          {cityBadgeText && <span className="tl-city-badge">{cityBadgeText}</span>}
                          <span className="tl-date-badge">{ev.dateStr}</span>
                          {timeStr && (
                            <span className="tl-time-badge">
                              {!isFreeDay && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                              )}
                              {timeStr}
                            </span>
                          )}
                        </div>
                        <div className="tl-card-header-right">
                          <span className="tl-type-badge">
                            {badgeType === 'Transfer' && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="8" rx="2" ry="2"></rect><path d="M3 11l2-5h14l2 5"></path><circle cx="7" cy="19" r="2"></circle><circle cx="17" cy="19" r="2"></circle></svg>
                            )}
                            {badgeType === 'Tour' && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                            )}
                            {badgeType}
                          </span>
                          {/* <span className="tl-smart-badge">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fdb813" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 8 21 9 16 14 17 21 12 17 7 21 8 14 3 9 9 8" /></svg>
                            Smart Suggestions
                          </span> */}
                        </div>
                      </div>
                      <div className="tl-card-body">
                        <div className="tl-main-title">
                          {mainIcon}
                          <h4>{title}</h4>
                        </div>
                        {isFreeDay ? (
                          <div className="tl-desc"><p>{desc}</p></div>
                        ) : (
                          <div className="tl-desc" dangerouslySetInnerHTML={{ __html: desc }}></div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* ✅ ==================== PDF Custom Sections ==================== */}
      {pdfSections && Object.keys(pdfSections).length > 0 && (
        <div className="custom-sections-new">
          {Object.entries(pdfSections).map(([key, section]) => {
            const sectionTitle = section.title || key;
            const titleLower = sectionTitle.toLowerCase();
            const isInc = titleLower.includes('inclusion') || sectionTitle.includes('يشمل');
            const isExc = titleLower.includes('exclusion') || sectionTitle.includes('لا يشمل') || sectionTitle.includes('مستثنى');
            
            let cardClass = "pdf-custom-card";
            if (isInc) cardClass += " inc-card";
            if (isExc) cardClass += " exc-card";
            
            let wrapperClass = "pdf-custom-section-new";
            if (!isInc && !isExc) wrapperClass += " full-width-section";
            
            let liClass = "";
            if (isInc) liClass = "inc-item";
            if (isExc) liClass = "exc-item";

            return (
              <div key={key} className={wrapperClass}>
                <div className={cardClass}>
                  <div className="inc-exc-header">
                    {isInc && (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    )}
                    {isExc && (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    )}
                    {!isInc && !isExc && (
                       <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                    )}
                    <h3>{sectionTitle}</h3>
                  </div>
                  
                  {section.items && section.items.length > 0 && (
                    <ul className="section-items-list-new">
                      {section.items.map((item, idx) => (
                        <li key={idx} className={liClass}>
                          {isInc ? (
                            <svg className="list-icon" width="8" height="8" viewBox="0 0 24 24" fill="#10b981"><circle cx="12" cy="12" r="12"/></svg>
                          ) : isExc ? (
                            <svg className="list-icon" width="8" height="8" viewBox="0 0 24 24" fill="#ef4444"><circle cx="12" cy="12" r="12"/></svg>
                          ) : (
                            <svg className="list-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                          <span>{t(item)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.note && (
                    <div className="section-note-new">
                      <strong>{lang === 'ar' ? 'ملاحظة' : 'Note'}:</strong> {section.note}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ ==================== Pricing Section ==================== */}
      {/* {includePrices && totalPrice > 0 && (
        <div className="pricing-section">
          <div className="total-price" dir={lang === 'ar' ? 'ltr' : 'rtl'}>
            <span className="price-amount">
              {formatCurrency(totalPrice)} {texts.currency}
            </span>
            <span>
              {lang === 'ar' ? 'السعر الإجمالي للبرنامج' : 'Total Program Price'}
            </span>
          </div>
        </div>
      )} */}

      {/* ==================== Footer ==================== */}
      <div className="print-footer">
        <p className="print-footer-text">
          {texts.companyName} • {texts.companyTagline}
        </p>
        <p className="print-footer-text" style={{ fontSize: '6pt', marginTop: '2px' }}>
          {lang === 'ar'
            ? 'تم إنشاء هذا البرنامج بواسطة نظام Adventures Club'
            : 'Generated by Adventures Club System'}
        </p>
      </div>
    </div>
  );
};

export default PrintProgram;
